import { useState } from "react";
import { OctagonX, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export function EmergencyStop({ active, onChange }) {
  const { can, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [flatten, setFlatten] = useState(false);
  if (!can("emergency_stop")) return null;

  const submit = async () => {
    try {
      const { data } = await api.post("/risk/emergency-stop", { active: !active, close_positions: flatten });
      toast[data.emergency_stop ? "error" : "success"](data.emergency_stop ? `Emergency Stop ACTIVE — new orders blocked, ${data.closed} positions closed` : "Emergency Stop cleared");
      if (data.errors?.length) toast.warning(data.errors.join("; "));
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <>
      <button
        data-testid="emergency-stop-button"
        onClick={() => setOpen(true)}
        disabled={active && user.role !== "owner"}
        className={`flex items-center gap-2 rounded-md px-4 py-2 font-display text-sm font-bold uppercase tracking-wider transition-transform duration-150 active:scale-95 ${active ? "border border-[hsl(var(--up)/0.5)] bg-transparent text-up hover:bg-[hsl(var(--up)/0.1)]" : "bg-[hsl(0_84%_60%)] text-white shadow-[0_0_24px_hsl(0_84%_60%/0.45)] hover:bg-[hsl(0_84%_52%)] hover:text-white"}`}
      >
        {active ? <ShieldCheck size={16} /> : <OctagonX size={16} />}
        {active ? "Clear Stop" : "Emergency Stop"}
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="emergency-stop-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase">{active ? "Clear Emergency Stop?" : "Activate Emergency Stop?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {active ? "Trading will remain disabled until you re-enable the engine manually." : "This immediately disables the automated engine and blocks every new order. Open positions stay under controlled management (stops, targets, manual modify/close) unless you choose to flatten."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!active && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={flatten} onChange={(e) => setFlatten(e.target.checked)} data-testid="emergency-flatten-checkbox" /> Also close all open positions now</label>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="emergency-stop-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="emergency-stop-confirm" onClick={submit} className={active ? "" : "bg-[hsl(0_84%_60%)] text-white hover:bg-[hsl(0_84%_52%)]"}>
              {active ? "Clear" : "Stop everything"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
